import React, { useMemo, useRef, useState } from 'react';
import { ActionIcon, Box, Group, Paper, ScrollArea, Text, Textarea } from '@mantine/core';
import { IconAt } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

export type MentionUser = {
  id: string;
  name: string;
};

type MentionTextareaProps = {
  value: string;
  onChange: (value: string) => void;
  users: MentionUser[];
  label?: string;
  placeholder?: string;
  minRows?: number;
  required?: boolean;
  mb?: string | number;
};

const MENTION_TOKEN_PATTERN = /@\[[0-9a-fA-F-]{36}:([^\]]+)\]/g;

export function formatMentionText(text: string): string {
  return text.replace(MENTION_TOKEN_PATTERN, '@$1');
}

export function buildMentionToken(user: MentionUser): string {
  return `@[${user.id}:${user.name}]`;
}

function findActiveMention(value: string, cursorPos: number) {
  const textBeforeCursor = value.slice(0, cursorPos);
  const match = textBeforeCursor.match(/@([^[\]]*)$/);
  if (!match) {
    return null;
  }
  return {
    query: match[1].trim().toLowerCase(),
    start: cursorPos - match[0].length,
    end: cursorPos,
  };
}

export function MentionTextarea({
  value,
  onChange,
  users,
  label,
  placeholder,
  minRows = 3,
  required = false,
  mb,
}: MentionTextareaProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionEnd, setMentionEnd] = useState<number | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);

  const filteredUsers = useMemo(() => {
    if (!mentionQuery) {
      return users;
    }
    return users.filter((user) => user.name.toLowerCase().includes(mentionQuery));
  }, [mentionQuery, users]);

  const closeMentionPicker = () => {
    setMentionOpen(false);
    setMentionQuery('');
    setMentionStart(null);
    setMentionEnd(null);
    setHighlightIndex(0);
  };

  const openMentionPicker = (query = '', start?: number, end?: number) => {
    const textarea = textareaRef.current;
    const cursorPos = end ?? textarea?.selectionStart ?? value.length;
    const tokenStart = start ?? cursorPos;
    setMentionOpen(true);
    setMentionQuery(query);
    setMentionStart(tokenStart);
    setMentionEnd(cursorPos);
    setHighlightIndex(0);
  };

  const syncMentionState = (nextValue: string, cursorPos: number) => {
    const activeMention = findActiveMention(nextValue, cursorPos);
    if (activeMention) {
      setMentionOpen(true);
      setMentionQuery(activeMention.query);
      setMentionStart(activeMention.start);
      setMentionEnd(activeMention.end);
      setHighlightIndex(0);
      return;
    }
    closeMentionPicker();
  };

  const insertMention = (user: MentionUser) => {
    const textarea = textareaRef.current;
    const start = mentionStart ?? textarea?.selectionStart ?? value.length;
    const end = mentionEnd ?? textarea?.selectionStart ?? value.length;
    const token = `${buildMentionToken(user)} `;
    const nextValue = `${value.slice(0, start)}${token}${value.slice(end)}`;
    onChange(nextValue);
    closeMentionPicker();

    requestAnimationFrame(() => {
      if (!textarea) {
        return;
      }
      const nextCursor = start + token.length;
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.currentTarget.value;
    const cursorPos = event.currentTarget.selectionStart ?? nextValue.length;
    onChange(nextValue);
    syncMentionState(nextValue, cursorPos);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!mentionOpen || filteredUsers.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((current) => (current + 1) % filteredUsers.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex(
        (current) => (current - 1 + filteredUsers.length) % filteredUsers.length,
      );
      return;
    }

    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      insertMention(filteredUsers[highlightIndex]);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeMentionPicker();
    }
  };

  const handleAtButtonClick = () => {
    const textarea = textareaRef.current;
    if (!textarea) {
      openMentionPicker('', value.length, value.length);
      onChange(`${value}@`);
      return;
    }

    const start = textarea.selectionStart ?? value.length;
    const end = textarea.selectionEnd ?? start;
    const nextValue = `${value.slice(0, start)}@${value.slice(end)}`;
    onChange(nextValue);
    openMentionPicker('', start, start + 1);

    requestAnimationFrame(() => {
      const nextCursor = start + 1;
      textarea.focus();
      textarea.setSelectionRange(nextCursor, nextCursor);
    });
  };

  return (
    <Box mb={mb} style={{ position: 'relative' }}>
      <Group spacing="xs" position="apart" mb={4}>
        {label ? (
          <Text size="sm" weight={500}>
            {label}
            {required ? ' *' : ''}
          </Text>
        ) : (
          <span />
        )}
        <ActionIcon
          variant="light"
          color="green"
          size="sm"
          title={t('tasks.mentions.pickUser')}
          onClick={handleAtButtonClick}
        >
          <IconAt size={16} />
        </ActionIcon>
      </Group>

      <Textarea
        ref={textareaRef}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={(event) =>
          syncMentionState(
            event.currentTarget.value,
            event.currentTarget.selectionStart ?? event.currentTarget.value.length,
          )
        }
        minRows={minRows}
        required={required}
      />

      {mentionOpen && (
        <Paper
          withBorder
          shadow="md"
          radius="md"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 4,
            zIndex: 300,
          }}
        >
          <ScrollArea style={{ maxHeight: 180 }}>
            {filteredUsers.length === 0 ? (
              <Text size="sm" color="dimmed" p="sm">
                {t('tasks.mentions.noUsers')}
              </Text>
            ) : (
              filteredUsers.map((user, index) => (
                <Box
                  key={user.id}
                  px="sm"
                  py={8}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    insertMention(user);
                  }}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: index === highlightIndex ? 'rgba(48,113,91,0.12)' : undefined,
                  }}
                >
                  <Text size="sm">{user.name}</Text>
                </Box>
              ))
            )}
          </ScrollArea>
        </Paper>
      )}
    </Box>
  );
}
